import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  IconDownload,
  IconPlayerPlay,
  IconPhone,
  IconPhoneIncoming,
  IconPhoneOutgoing,
  IconClock,
  IconUser
} from '@tabler/icons-react';
import { callAPI } from '../../services/api';
import { userAPI } from '../../services/api';
import * as XLSX from 'xlsx-js-style';

interface CallReportsProps {
  startDate: string;
  endDate: string;
}

const CallReports: React.FC<CallReportsProps> = ({ startDate, endDate }) => {
  const [calls, setCalls] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCalls, setTotalCalls] = useState(0);
  const [agents, setAgents] = useState<any[]>([]);
  
  // Filters
  const [filterDirection, setFilterDirection] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterQueue, setFilterQueue] = useState('');
  const [filterPhone, setFilterPhone] = useState('');

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    loadCalls();
  }, [startDate, endDate, page, rowsPerPage, filterDirection, filterStatus, filterAgent, filterQueue, filterPhone]);

  const loadAgents = async () => {
    try {
      const response = await userAPI.getAll();
      if (response.data.success) {
        const agentUsers = response.data.data.filter((u: any) => u.role === 'agent');
        setAgents(agentUsers);
      }
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadCalls = async () => {
    setLoading(true);
    try {
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'startTime',
        sortOrder: 'desc'
      };

      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (filterDirection !== 'all') filters.direction = filterDirection;
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterAgent !== 'all') {
        const selectedAgent = agents.find(a => a._id === filterAgent);
        if (selectedAgent) {
          filters.agentName = `${selectedAgent.firstName} ${selectedAgent.lastName}`;
        }
      }
      if (filterQueue) filters.queueName = filterQueue;
      if (filterPhone) filters.phoneNumber = filterPhone;

      const response = await callAPI.getAll(filters);
      
      if (response.data.success) {
        setCalls(response.data.data || []);
        setTotalCalls(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error loading calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'answered': return '#10b981';
      case 'missed': return '#ef4444';
      case 'failed': return '#f59e0b';
      case 'ended': return '#6b7280';
      default: return '#3b82f6';
    }
  };

  const exportToExcel = async () => {
    try {
      // Tüm veriyi filtrelerle çek (limit yok)
      const filters: any = {
        sortBy: 'startTime',
        sortOrder: 'desc',
        limit: 10000 // Maksimum limit
      };

      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (filterDirection !== 'all') filters.direction = filterDirection;
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterAgent !== 'all') {
        const selectedAgent = agents.find(a => a._id === filterAgent);
        if (selectedAgent) {
          filters.agentName = `${selectedAgent.firstName} ${selectedAgent.lastName}`;
        }
      }
      if (filterQueue) filters.queueName = filterQueue;
      if (filterPhone) filters.phoneNumber = filterPhone;

      const response = await callAPI.getAll(filters);
      const allCalls = response.data.success ? (response.data.data || []) : [];

      if (allCalls.length === 0) {
        alert('Dışa aktarılacak veri bulunamadı');
        return;
      }

      const exportData = allCalls.map((call: any, index: number) => ({
        'Sıra': index + 1,
        'Tarih/Saat': new Date(call.startTime).toLocaleString('tr-TR'),
        'Arayan': call.callerNumber || '-',
        'Aranan': call.calledNumber || '-',
        'Agent': call.agentId?.firstName && call.agentId?.lastName 
          ? `${call.agentId.firstName} ${call.agentId.lastName}` 
          : call.agentId?.email || '-',
        'Kuyruk': call.queueId?.name || '-',
        'Yön': call.direction === 'inbound' ? 'Gelen' : 'Giden',
        'Durum': call.status || '-',
        'Süre': formatDuration(call.duration),
        'Bekleme': formatDuration(call.waitTime),
        'Finish Code': call.finishCode || '-'
      }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Stil uygula
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Header stilleri
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        fill: { fgColor: { rgb: "4F46E5" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Tüm hücreleri ortala
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[address]) continue;
        if (!ws[address].s) ws[address].s = {};
        ws[address].s.alignment = { horizontal: "center", vertical: "center" };
      }
    }

    // Sütun genişlikleri
    ws['!cols'] = [
      { wch: 8 },  // Sıra
      { wch: 20 }, // Tarih/Saat
      { wch: 15 }, // Arayan
      { wch: 15 }, // Aranan
      { wch: 20 }, // Agent
      { wch: 15 }, // Kuyruk
      { wch: 10 }, // Yön
      { wch: 12 }, // Durum
      { wch: 10 }, // Süre
      { wch: 10 }, // Bekleme
      { wch: 15 }  // Finish Code
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Çağrı Raporu');
    XLSX.writeFile(wb, `Cagri_Raporu_${startDate}_${endDate}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Yön</InputLabel>
                <Select
                  value={filterDirection}
                  onChange={(e) => setFilterDirection(e.target.value)}
                  label="Yön"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="inbound">Gelen</MenuItem>
                  <MenuItem value="outbound">Giden</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Durum</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Durum"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="answered">Cevaplanmış</MenuItem>
                  <MenuItem value="completed">Tamamlanmış</MenuItem>
                  <MenuItem value="ended">Sonlandırılmış</MenuItem>
                  <MenuItem value="missed">Kaçan</MenuItem>
                  <MenuItem value="no-answer">Cevapsız</MenuItem>
                  <MenuItem value="failed">Başarısız</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Agent</InputLabel>
                <Select
                  value={filterAgent}
                  onChange={(e) => setFilterAgent(e.target.value)}
                  label="Agent"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  {agents.map((agent) => (
                    <MenuItem key={agent._id} value={agent._id}>
                      {agent.firstName} {agent.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Telefon Ara"
                value={filterPhone}
                onChange={(e) => setFilterPhone(e.target.value)}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Toplam {totalCalls} çağrı bulundu
            </Typography>
            <Button
              variant="contained"
              startIcon={<IconDownload size={18} />}
              onClick={exportToExcel}
              disabled={calls.length === 0}
            >
              Excel İndir
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Calls Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Tarih/Saat</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Arayan</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Aranan</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Agent</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Kuyruk</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Yön</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Durum</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Süre</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Bekleme</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Finish Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Kayıt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calls.map((call) => (
                      <TableRow key={call._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {new Date(call.startTime).toLocaleString('tr-TR')}
                          </Typography>
                        </TableCell>
                        <TableCell>{call.callerNumber || '-'}</TableCell>
                        <TableCell>{call.calledNumber || '-'}</TableCell>
                        <TableCell>
                          {call.agentId?.firstName && call.agentId?.lastName 
                            ? `${call.agentId.firstName} ${call.agentId.lastName}` 
                            : call.agentId?.email || '-'}
                        </TableCell>
                        <TableCell>{call.queueId?.name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={call.direction === 'inbound' ? <IconPhoneIncoming size={14} /> : <IconPhoneOutgoing size={14} />}
                            label={call.direction === 'inbound' ? 'Gelen' : 'Giden'}
                            size="small"
                            color={call.direction === 'inbound' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={call.status}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(call.status),
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDuration(call.duration)}</TableCell>
                        <TableCell>{formatDuration(call.waitTime)}</TableCell>
                        <TableCell>{call.finishCode || '-'}</TableCell>
                        <TableCell>
                          {call.recordingPath && (
                            <Tooltip title="Kaydı Dinle">
                              <IconButton size="small" color="primary">
                                <IconPlayerPlay size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCalls}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Sayfa başına:"
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CallReports;
